import { useEffect, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AdmissionProvider } from '@cdn/admission';
import { ApiProvider } from '@cdn/api';
import { DocsProvider } from '@cdn/docs';
import { FamilyProvider } from '@cdn/family';
import { I18nProvider } from '@cdn/i18n';
import { IdentityProvider } from '@cdn/identity';
import { RealtimeProvider } from '@cdn/realtime';
import { RouteFocus, ThemeProvider } from '@cdn/shell';
import { OutcomeProvider } from '@cdn/ui';
import { VisitProvider } from '@cdn/visit';
import type { Composition } from './composition';
import { Routing } from './routing';

/**
 * Provides the composed objects to the tree below it.
 */
export function Providers({ composition, children }: { composition: Composition; children: ReactNode }) {
	return (
		<I18nProvider i18n={composition.i18n}>
			<ThemeProvider theme={composition.theme}>
				<DocsProvider docs={composition.docs}>
					<ApiProvider api={composition.api}>
						<IdentityProvider session={composition.session}>
							<AdmissionProvider admission={composition.admission}>
								<RealtimeProvider realtime={composition.realtime}>
									<FamilyProvider family={composition.family}>
										<OutcomeProvider>
											<VisitProvider delivery={composition.delivery}>{children}</VisitProvider>
										</OutcomeProvider>
									</FamilyProvider>
								</RealtimeProvider>
							</AdmissionProvider>
						</IdentityProvider>
					</ApiProvider>
				</DocsProvider>
			</ThemeProvider>
		</I18nProvider>
	);
}

/**
 * The application: providers, the browser router and the route table. It restores the session once.
 */
export function App({ composition }: { composition: Composition }) {
	const { session } = composition;
	useEffect(() => {
		session.status === 'restoring' && void session.restore();
	}, [session]);

	return (
		<Providers composition={composition}>
			<BrowserRouter>
				<RouteFocus />
				<Routing />
			</BrowserRouter>
		</Providers>
	);
}
